import { useQuery } from '@apollo/client';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import useAppSelector from '../app/redux/useAppSelector';
import { gql } from '../graphql/generated/gql';
import { UserType } from '../graphql/generated/graphql';
import updateSubscribedList from '../utils/graphql/updateSubscribedList';
import useIsPublicView from '../utils/useIsPublicView';

// eslint-disable-next-line slab-graphql/no-unused-fields
gql(`
  fragment allSeries_series on SeriesFields {
    id
    name
    posts {
      id
      title
    }
  }
`);

const ALL_SERIES = gql(/** @complexity 8 */ `
  query allSeries {
    session: currentSession {
      organization {
        id
        series {
          id
          ...allSeries_series
        }
      }
    }
  }
`);

const SERIES_CREATED_SUBSCRIPTION = gql(/** @complexity 12 */ `
  subscription seriesCreated {
    series: seriesCreated {
      ...allSeries_series
    }
  }
`);

const SERIES_DELETED_SUBSCRIPTION = gql(/** @complexity 12 */ `
  subscription seriesDeleted {
    series: seriesDeleted {
      ...allSeries_series
    }
  }
`);

const SERIES_UPDATED_SUBSCRIPTION = gql(/** @complexity 12 */ `
  subscription seriesUpdated {
    series: seriesUpdated {
      ...allSeries_series
    }
  }
`);

interface UseAllSeriesQueryOptions {
  skip?: boolean;
}

const useAllSeriesQuery = (options?: UseAllSeriesQueryOptions) => {
  const skip = !!options?.skip;
  const { data, subscribeToMore } = useQuery(ALL_SERIES, { skip });

  useEffect(() => {
    if (skip) {
      return undefined;
    }
    const unsubscribes = [
      SERIES_CREATED_SUBSCRIPTION,
      SERIES_DELETED_SUBSCRIPTION,
      SERIES_UPDATED_SUBSCRIPTION,
    ].map((query) =>
      subscribeToMore({
        document: query,
        updateQuery: updateSubscribedList(
          'session.organization.series',
          'series',
          query !== SERIES_DELETED_SUBSCRIPTION,
        ),
      }),
    );

    return () => {
      unsubscribes.forEach((unsubscribe) => {
        unsubscribe();
      });
    };
  }, [skip, subscribeToMore]);

  return data?.session.organization.series ?? [];
};

const AllSeriesContext = createContext<{
  series: ReturnType<typeof useAllSeriesQuery>;
  query: () => void;
}>({ series: [], query: () => {} });

interface AllSeriesProviderProps {
  children: React.ReactNode;
}

export const AllSeriesProvider = ({ children }: AllSeriesProviderProps) => {
  const [isIdle, setIsIdle] = useState(true);

  const series = useAllSeriesQuery({ skip: isIdle });

  const isLoggedIn = !!useAppSelector(
    (state) =>
      state.session.user.id && state.session.user.type !== UserType.Visitor,
  );
  const isPublicView = useIsPublicView();

  const value = useMemo(
    () => ({ query: () => setIsIdle(!isLoggedIn || isPublicView), series }),
    [isLoggedIn, isPublicView, series],
  );

  return (
    <AllSeriesContext.Provider value={value}>
      {children}
    </AllSeriesContext.Provider>
  );
};

const useAllSeries = () => {
  const { series, query } = useContext(AllSeriesContext);

  useEffect(() => {
    query();
  }, [query]);

  return series;
};

export const usePostSeries = (postId: string) => {
  const allSeries = useAllSeries();
  return allSeries.find(({ posts }) => posts.some(({ id }) => id === postId));
};
